import {
  SIDEBAR_VISIBLE,
  SIDEBAR_ACTIVE_MENUS,
} from '../../constants/cookieKeys'
import React from 'react'
import { Dropdown, Image, Icon, InitialsAvatar } from '@adc/ui-core'
import { getLogoutUrl } from '../../utils/getLogoutUrl'
import { RIGHTS, RESOURCES } from '../../constants/permissions'
import classnames from 'classnames/bind'
import inlineLogo from '../../images/logo/morpheus/inline-light-v2.svg'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const NavigationChevron = (
  <div className={cx('u-ml-1', 'u-display-ib')}>
    <Icon name='chevron down' size='small' color='white' />
  </div>
)

const getInitialsFromName = (name: string) => {
  const nameParts = name.trim().split(' ')
  if (nameParts.length === 1) {
    return nameParts[0].charAt(0).toUpperCase()
  }
  const firstInitial = nameParts[0].charAt(0).toUpperCase()
  const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase()
  return firstInitial + lastInitial
}

interface NavigationHeader2026Props {
  cookies?: {
    remove: (key: string, options: { path: string }) => void
  }
  customLogo?: string
  displayName?: string
  featureFlags?: Record<string, boolean>
  permissions: {
    hasAccess?: (resource: string, right: string) => boolean
  }
}

const NavigationHeader2026: React.FC<NavigationHeader2026Props> = ({
  cookies,
  customLogo,
  displayName,
  featureFlags = {},
  permissions,
}) => {
  const onLogout = () => {
    if (cookies) {
      cookies.remove(SIDEBAR_ACTIVE_MENUS, { path: '/' })
      cookies.remove(SIDEBAR_VISIBLE, { path: '/' })
    }
    document.location.href = getLogoutUrl()
  }

  const experimentalHeaderActive = true

  const hasPermissions = (resource: string) => {
    return permissions.hasAccess && permissions.hasAccess(resource, RIGHTS.READ)
  }

  const shouldShowFinanceSection =
    featureFlags['fe.resi.is_morpheus_finance_section_enabled'] &&
    hasPermissions(RESOURCES.FINANCE_PAGE)

  const shouldShowLiveAuctionCheckInOutTracker =
    featureFlags['fe.resi.is_live_auction_check_in_out_tracker_enabled'] &&
    hasPermissions(RESOURCES.AUCTIONEER_LIVE_AUCTION_TRACKER_PAGE)

  const shouldShowOrganizationsMenu = hasPermissions(
    RESOURCES.ORGANIZATIONS_PAGE
  )

  const shouldShowCommunicationsMenu =
    featureFlags['fe.resi.is_communications_enabled'] &&
    hasPermissions(RESOURCES.COMMUNICATIONS_PAGE)

  // Build menu item arrays for each section
  const onboardingAccessMenuItems = [
    hasPermissions(RESOURCES.USERS_PAGE) && (
      <Dropdown.Item
        key='users'
        as='a'
        text='Users'
        href='/user-management'
        data-elm-id='menu_item_users'
      />
    ),
    featureFlags['fe.resi.invitations-management'] &&
      hasPermissions(RESOURCES.INVITATIONS_PAGE) && (
        <Dropdown.Item
          key='invitations'
          as='a'
          text='Invitations'
          href='/invitations'
          data-elm-id='menu_item_invitations'
        />
      ),
    shouldShowCommunicationsMenu && (
      <Dropdown.Item
        key='communications'
        as='a'
        text='Communications'
        href='/communications'
        data-elm-id='menu_item_communications'
      />
    ),
    ...(shouldShowOrganizationsMenu
      ? [
          <Dropdown.Header key='org-header' content='Organizations' />,
          <Dropdown.Item
            key='organizations'
            as='a'
            text='Manage'
            href='/organizations'
            data-elm-id='menu_item_organizations'
          />,
          <Dropdown.Item
            key='organization-members'
            as='a'
            text='Members'
            href='/organizations/members'
            data-elm-id='menu_item_organization_members'
          />,
          <Dropdown.Item
            key='activity-timeline'
            as='a'
            text='Activity Timeline'
            href='/organizations/activitytimeline'
            data-elm-id='menu_item_activity_timeline'
          />,
        ]
      : []),
  ].filter(Boolean)

  const assetManagementMenuItems = [
    hasPermissions(RESOURCES.INTAKE_HUB_PAGE) && (
      <Dropdown.Item
        key='intake-hub'
        as='a'
        text='Intake Hub'
        href='/property/intake'
        data-elm-id='menu_item_intake_hub'
      />
    ),
    featureFlags['fe.resi.enable-leads-page'] && (
      <Dropdown.Item
        key='leads'
        as='a'
        text='Leads'
        href='/property/leads'
        data-elm-id='menu_item_leads'
      />
    ),
    featureFlags['fe.resi.enable-transactions-view'] &&
      hasPermissions(RESOURCES.TRANSACTIONS_PAGE) && (
        <Dropdown.Item
          key='transactions'
          as='a'
          text='Transactions'
          href='/transactions'
          data-elm-id='menu_item_transactions'
        />
      ),
    hasPermissions(RESOURCES.PROPERTIES_PAGE) && (
      <Dropdown.Item
        key='properties'
        as='a'
        text='Properties'
        href='/property/properties'
        data-elm-id='menu_item_properties'
      />
    ),
    hasPermissions(RESOURCES.LISTINGS_PAGE) && (
      <Dropdown.Item
        key='listings'
        as='a'
        text='Listings'
        href='/property/listings'
        data-elm-id='menu_item_listings'
      />
    ),
    hasPermissions(RESOURCES.EVENTS_PAGE) && (
      <Dropdown.Item
        key='events'
        as='a'
        text='Events'
        href='/schedule/events'
        data-elm-id='menu_item_events'
      />
    ),
    hasPermissions(RESOURCES.VENUES_PAGE) && (
      <Dropdown.Item
        key='venues'
        as='a'
        text='Venues'
        href='/schedule/venues'
        data-elm-id='menu_item_venues'
      />
    ),
  ].filter(Boolean)

  const operationsMenuItems = [
    hasPermissions(RESOURCES.AUCTIONEER_DASHBOARD_PAGE) && (
      <Dropdown.Item
        key='auctioneer-dashboard'
        as='a'
        text='Auctioneer Dashboard'
        href='/auctioneer-dashboard'
        data-elm-id='menu_item_auctioneer_dashboard'
      />
    ),
    shouldShowLiveAuctionCheckInOutTracker && (
      <Dropdown.Item
        key='live-auction-tracker'
        as='a'
        text='Live Auction Check In/Out'
        href='/auctioneer-dashboard/live-auction-tracker'
        data-elm-id='menu_item_live_auction_tracker'
      />
    ),
    hasPermissions(RESOURCES.TRANSACTION_MANAGER_PAGE) && (
      <Dropdown.Item
        key='contracting'
        as='a'
        text='Contracting'
        href='/property/contracting'
        data-elm-id='menu_item_contracting'
      />
    ),
    hasPermissions(RESOURCES.TRANSACTION_MANAGER_PAGE) && (
      <Dropdown.Item
        key='closings'
        as='a'
        text='Closing'
        href='/property/closings'
        data-elm-id='menu_item_closings'
      />
    ),
  ].filter(Boolean)

  const configurationMenuItems = [
    hasPermissions(RESOURCES.EVENT_TEMPLATES_PAGE) && (
      <Dropdown.Item
        key='event-templates'
        as='a'
        text='Event Templates'
        href='/schedule/event-templates'
        data-elm-id='menu_item_event_templates'
      />
    ),
    hasPermissions(RESOURCES.VENUE_TEMPLATES_PAGE) && (
      <Dropdown.Item
        key='venue-templates'
        as='a'
        text='Venue Templates'
        href='/schedule/venue-templates'
        data-elm-id='menu_item_venue_templates'
      />
    ),
    hasPermissions(RESOURCES.EMAIL_TEMPLATE_MANAGEMENT_PAGE) && (
      <Dropdown.Item
        key='email-templates'
        as='a'
        text='Email Templates'
        href='/email-template-management'
        data-elm-id='menu_item_email_templates'
      />
    ),
    hasPermissions(RESOURCES.DOCUMENTS_PAGE) && (
      <Dropdown.Item
        key='documents'
        as='a'
        text='Documents'
        href='/property/documents'
        data-elm-id='menu_item_documents'
      />
    ),
    hasPermissions(RESOURCES.PACKAGES_PAGE) && (
      <Dropdown.Item
        key='packages'
        as='a'
        text='Packages'
        href='/property/packages'
        data-elm-id='menu_item_packages'
      />
    ),
    hasPermissions(RESOURCES.LISTING_ATTRIBUTES_PAGE) && (
      <Dropdown.Item
        key='listing-attributes'
        as='a'
        text='Listing Attributes'
        href='/property/listing-attributes'
        data-elm-id='menu_item_listing_attributes'
      />
    ),
    hasPermissions(RESOURCES.MARKETING_TAGS_PAGE) && (
      <Dropdown.Item
        key='marketing-tags'
        as='a'
        text='Marketing Tags'
        href='/property/marketing-tags'
        data-elm-id='menu_item_marketing_tags'
      />
    ),
    hasPermissions(RESOURCES.TINY_URL_PAGE) && (
      <Dropdown.Item
        key='tiny-urls'
        as='a'
        text='Tiny Urls'
        href='/tiny-url'
        data-elm-id='menu_item_tiny_urls'
      />
    ),
    hasPermissions(RESOURCES.CLIENT_AUTHORIZATION_PAGE) && (
      <Dropdown.Item
        key='client-authorization'
        as='a'
        text='Client Authorization'
        href='/client-authorization'
        data-elm-id='menu_item_client_authorization'
      />
    ),
  ].filter(Boolean)

  const insightsMenuItems = [
    hasPermissions(RESOURCES.MORPHEUS_LOGS_PAGE) && (
      <Dropdown.Item
        key='morpheus-logs'
        as='a'
        text='Morpheus Logs'
        href='/sync/morpheus-logs'
        data-elm-id='menu_item_morpheus'
      />
    ),
    shouldShowFinanceSection && (
      <Dropdown.Item
        key='transaction-history'
        as='a'
        text='Transaction History'
        href='/finance/transaction-history'
        data-elm-id='menu_item_transaction-history'
      />
    ),
    hasPermissions(RESOURCES.AI_EVALUATIONS_PAGE) && (
      <Dropdown.Item
        key='ai-evaluations'
        as='a'
        text='AI Evaluations'
        href='/ai-evaluations'
        data-elm-id='menu_item_ai_evaluations'
      />
    ),
    <Dropdown.Item
      key='communication-templates'
      as='a'
      text='Communication Templates'
      href='/comgen'
      data-elm-id='menu_item_communication_templates'
    />,
  ].filter(Boolean)

  return (
    <div
      className={cx('container', { experimental: experimentalHeaderActive })}
    >
      <div
        className={cx('left-part', {
          experimental: experimentalHeaderActive,
        })}
      >
        <a href='/' data-elm-id='home_link'>
          <Image
            className={cx('icon')}
            src={customLogo || inlineLogo}
            size='small'
          />
        </a>

        <nav className={cx('u-ml-4')}>
          {onboardingAccessMenuItems.length > 0 && (
            <section className={cx('u-mr-3')}>
              <Dropdown
                trigger='Onboarding & Access'
                floating
                icon={NavigationChevron}
                data-elm-id='onboarding_access_navigation_tab'
              >
                <Dropdown.Menu position='right'>
                  {onboardingAccessMenuItems}
                </Dropdown.Menu>
              </Dropdown>
            </section>
          )}
          {assetManagementMenuItems.length > 0 && (
            <section className={cx('u-mr-3')}>
              <Dropdown
                trigger='Asset Management'
                floating
                icon={NavigationChevron}
                data-elm-id='asset_management_navigation_tab'
              >
                <Dropdown.Menu>{assetManagementMenuItems}</Dropdown.Menu>
              </Dropdown>
            </section>
          )}
          {operationsMenuItems.length > 0 && (
            <section className={cx('u-mr-3')}>
              <Dropdown
                trigger='Operations'
                floating
                icon={NavigationChevron}
                data-elm-id='operations_navigation_tab'
              >
                <Dropdown.Menu>{operationsMenuItems}</Dropdown.Menu>
              </Dropdown>
            </section>
          )}
          {configurationMenuItems.length > 0 && (
            <section className={cx('u-mr-3')}>
              <Dropdown
                trigger='Configuration'
                floating
                icon={NavigationChevron}
                data-elm-id='configuration_navigation_tab'
              >
                <Dropdown.Menu>{configurationMenuItems}</Dropdown.Menu>
              </Dropdown>
            </section>
          )}
          {insightsMenuItems.length > 0 && (
            <section className={cx('u-mr-3')}>
              <Dropdown
                trigger='Insights'
                floating
                icon={NavigationChevron}
                data-elm-id='insights_navigation_tab'
              >
                <Dropdown.Menu>{insightsMenuItems}</Dropdown.Menu>
              </Dropdown>
            </section>
          )}
        </nav>
      </div>

      {/* <div className={cx('right-part', 'experimental')}> */}
      <div className={cx('right-part', 'experimental', 'avatar-menu')}>
        <Dropdown
          trigger={
            <InitialsAvatar
              className={cx('avatar')}
              initials={getInitialsFromName(displayName || '?')}
              identifier={displayName}
            />
          }
          floating
          // icon={NavigationChevron}
          data-elm-id='menu_user'
        >
          <Dropdown.Menu className={cx('menu-right-aligned')}>
            <Dropdown.Header>{displayName}</Dropdown.Header>
            <Dropdown.Item
              key='home'
              as='a'
              text='Home'
              href='/'
              data-elm-id='menu_item_home'
            />
            <Dropdown.Item
              key='chat-notebook'
              as='a'
              text='Chat Notebook'
              href='/chat-notebook'
              data-elm-id='menu_item_chat_notebook'
            />
            <Dropdown.Divider />
            <Dropdown.Item
              as='a'
              data-elm-id='menu_user_logout'
              text='Logout'
              onClick={onLogout}
            />
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  )
}

export default NavigationHeader2026
